import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MembersService } from 'src/app/shared/services/members.service';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { AttributesEditorDataSource } from 'src/shared-app-portal/components/attributes-editor/attributesEditorDataSource';
import {
  AnswerModel,
  LocalPersistenceService
} from 'src/shared-app-portal/services/local-persistence.service';
import { PositionPickerModel } from 'src/app/shared/models/application/positionPickerModel';
import { PersonPositionSearchResult } from 'src/app/people/models/personPositionSearchResult';
import { SnackBarService } from 'src/app/shared/services/snackbar.service';

/**
 * The controls the attributes editor can render for a seat field. Complex and document fields are
 * rejected as seat fields server-side, so their uploaders never appear here; a star rating has
 * nothing focusable at all, in which case focus is simply left where it was.
 */
const SEAT_FIELD_CONTROL_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), mat-select';

@Component({
  selector: 'app-position-person-picker',
  templateUrl: './position-person-picker.component.html',
  styleUrls: ['./position-person-picker.component.scss'],
  standalone: false
})
export class PositionPersonPickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() position: PositionPickerModel;
  /** the admin preview already shows the title in its panel header */
  @Input() showTitle = true;
  @Input() isTouched: boolean;
  @Input() initialSelectedPersonIds: string[] = [];
  @Input() invalidPositionPersonIds: string[] = [];
  /** personId -> seat field values already on that person's seat, used to prefill the editors */
  @Input() initialSeatFieldValues: Record<string, Attribute[]> = {};
  @Output() personIdsChange = new EventEmitter<string[]>();
  @Output() seatFieldValuesChange = new EventEmitter<{
    personId: string;
    attributes: AttributeWrite[];
    /** same values in saved-answer form, so a save-for-later draft can restore them */
    answers: AnswerModel[];
  }>();

  searchControl = new FormControl<string>('');
  searchResults: PersonPositionSearchResult[] = [];
  selectedPersons: PersonPositionSearchResult[] = [];
  isLoading = false;

  /** one editor per selected person; created on select, dropped on remove */
  private seatFieldDataSources = new Map<string, AttributesEditorDataSource>();

  private destroy$ = new Subject<void>();
  private pendingSeatFieldFocus: ReturnType<typeof setTimeout>;
  private invalidPersonIdSet = new Set<string>();
  private currentPage = 1;
  private currentSearchValue = '';
  private _inputFocused = false;
  /** index into searchResults of the option the keyboard is on; -1 when none */
  activeOptionIndex = -1;
  /** announced politely when someone is added or removed */
  selectionAnnouncement = '';
  /** set by Escape so the panel stays shut until the search changes again */
  private _dismissed = false;

  constructor(
    private membersService: MembersService,
    private snackService: SnackBarService,
    private localPersistenceService: LocalPersistenceService
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['isTouched'] && this.isTouched) {
      this.searchControl.markAsTouched();
    }
    if (changes['invalidPositionPersonIds']) {
      this.invalidPersonIdSet = new Set(this.invalidPositionPersonIds);
    }
    if (changes['initialSelectedPersonIds']) {
      if (this.initialSelectedPersonIds?.length && this.selectedPersons.length === 0) {
        //todo: if read-only no snack message?
        const results = await this.membersService.positionSearchLoadResults(
          this.position.id,
          this.initialSelectedPersonIds
        );
        if (results.selectedPersons.length) {
          this.selectedPersons = results.selectedPersons;
          if (results.warningMessage) {
            //any toasted error message will show up on page 1
            //Ideally we want to wait to wait till they're on the position step, but since this won't happen often going to leave it for now
            this.snackService.open(results.warningMessage, false);
          }
        }

        //send this.selectedPersons's Ids to the parent component now that they've validated from the API.

        this.emitPersonIds();
        this.syncSeatFieldDataSources();
      }
    }
  }

  ngOnInit(): void {
    if (this.position.minSeatCount > 0) {
      this.searchControl.addValidators(() =>
        this.selectedPersons.length >= this.position.minSeatCount ? null : { minSeats: true }
      );
      this.searchControl.updateValueAndValidity({ emitEvent: false });
    }

    this.searchControl.valueChanges
      .pipe(
        filter((v) => typeof v === 'string' && v.length >= 2),
        tap(() => (this.isLoading = true)),
        debounceTime(300),
        switchMap((v) => {
          this.searchResults = [];
          this._dismissed = false;
          this.currentPage = 1;
          this.currentSearchValue = v as string;
          return this.membersService
            .positionSearch(this.position.id, v as string, 1)
            .pipe(finalize(() => (this.isLoading = false)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.searchResults = results;
        this.activeOptionIndex = -1;
      });
  }

  ngOnDestroy(): void {
    clearTimeout(this.pendingSeatFieldFocus);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isDropdownOpen(): boolean {
    return (
      this._inputFocused &&
      !this._dismissed &&
      (this.isLoading ||
        this.searchResults.length > 0 ||
        (typeof this.searchControl.value === 'string' && this.searchControl.value.length >= 2))
    );
  }

  onInputFocus(): void {
    this._inputFocused = true;
  }

  /**
   * Closing on the input's own blur would tear the panel down before a Tab could land on the
   * "load more" button inside it. Focus moving anywhere within the control keeps the panel open;
   * only leaving the control entirely closes it.
   */
  onSearchFocusOut(event: FocusEvent): void {
    const control = event.currentTarget as HTMLElement;
    const nextFocused = event.relatedTarget as HTMLElement | null;
    if (nextFocused && control.contains(nextFocused)) {
      return;
    }

    this._inputFocused = false;
  }

  /** ids the combobox uses to point the input at its listbox and active option */
  get listboxId(): string {
    return `position-listbox-${this.position.id}`;
  }

  optionId(index: number): string {
    return `position-option-${this.position.id}-${index}`;
  }

  get activeOptionId(): string | null {
    return this.activeOptionIndex >= 0 ? this.optionId(this.activeOptionIndex) : null;
  }

  get searchInputLabel(): string {
    return `Search people for ${this.position.name}`;
  }

  get searchInputDescribedBy(): string | null {
    const ids: string[] = [];
    if (this.position.questionDescription) {
      ids.push(`${this.position.id}-description`);
    }
    if (this.hasDuplicatePersons) {
      ids.push(`duplicate-${this.position.id}`);
    }
    return ids.length ? ids.join(' ') : null;
  }

  get resultsAnnouncement(): string {
    if (!this.isDropdownOpen || this.isLoading) {
      return '';
    }

    if (this.searchResults.length === 0) {
      return 'No results found';
    }

    return `${this.searchResults.length} ${
      this.searchResults.length === 1 ? 'result' : 'results'
    } available. Use the up and down arrow keys to review, then press Enter to add.`;
  }

  /**
   * Keyboard support for the combobox. Focus deliberately stays on the input and the active option is
   * tracked with aria-activedescendant — moving real focus into the panel would fire the input's blur
   * and close it.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this._dismissed = true;
      this.activeOptionIndex = -1;
      return;
    }

    if (!this.isDropdownOpen || this.searchResults.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeOptionIndex = (this.activeOptionIndex + 1) % this.searchResults.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeOptionIndex =
          this.activeOptionIndex <= 0 ? this.searchResults.length - 1 : this.activeOptionIndex - 1;
        break;
      case 'Home':
        event.preventDefault();
        this.activeOptionIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        this.activeOptionIndex = this.searchResults.length - 1;
        break;
      case 'Enter':
        if (this.activeOptionIndex >= 0) {
          //only swallow Enter when it actually picks someone, so it can still submit otherwise
          event.preventDefault();
          this.selectPerson(this.searchResults[this.activeOptionIndex]);
        }
        return;
      default:
        return;
    }

    this.scrollActiveOptionIntoView();
  }

  /**
   * The panel scrolls once there are more results than fit, and focus never leaves the input, so the
   * browser won't bring the active option into view on its own.
   */
  private scrollActiveOptionIntoView(): void {
    const id = this.activeOptionId;
    if (!id) {
      return;
    }

    document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
  }

  selectPerson(person: PersonPositionSearchResult): void {
    if (!this.selectedPersons.find((p) => p.id === person.id)) {
      this.selectedPersons = [...this.selectedPersons, person];
      this.selectionAnnouncement = `${person.firstName} ${person.lastName} added to ${this.position.name}`;
      this.emitPersonIds();
      this.syncSeatFieldDataSources();
      this.searchControl.updateValueAndValidity({ emitEvent: false });
      this.focusFirstSeatField(person.id);
    }
    this.searchControl.setValue('', { emitEvent: false });
    this.searchResults = [];
    this.activeOptionIndex = -1;
  }

  loadMore(): void {
    this.currentPage++;
    this.isLoading = true;
    this.membersService
      .positionSearch(this.position.id, this.currentSearchValue, this.currentPage)
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.searchResults.push(...results);
      });
  }

  get showLoadMore(): boolean {
    return !this.isLoading && this.searchResults.length > 0 && this.searchResults.length % 10 === 0;
  }

  removePerson(person: PersonPositionSearchResult): void {
    this.selectedPersons = this.selectedPersons.filter((p) => p.id !== person.id);
    this.selectionAnnouncement = `${person.firstName} ${person.lastName} removed from ${this.position.name}`;
    this.seatFieldDataSources.delete(person.id);
    this.emitPersonIds();
    this.searchControl.updateValueAndValidity({ emitEvent: false });
  }

  get isInvalid(): boolean {
    return this.position.minSeatCount > this.selectedPersons.length && this.isTouched;
  }

  get hasDuplicatePersons(): boolean {
    return this.selectedPersons.some((p) => this.invalidPersonIdSet.has(p.id));
  }

  isPersonInvalid(person: PersonPositionSearchResult): boolean {
    return this.invalidPersonIdSet.has(person.id);
  }

  get hasSeatFields(): boolean {
    return this.position.seatFields?.length > 0;
  }

  /**
   * A read-only position shows its tracked values but can't change them, and the API ignores anything
   * submitted for one — so nothing is emitted and required is not enforced.
   */
  get seatFieldsReadOnly(): boolean {
    return this.position.isReadOnly;
  }

  /**
   * Builds an editor for every selected person and drops any left over. Newly built editors publish
   * their values immediately — a prefilled person's existing values would otherwise never reach the
   * submit payload unless the applicant happened to touch that field, which fails the server's
   * required check for people who were already on the team.
   */
  private syncSeatFieldDataSources(): void {
    if (!this.hasSeatFields) {
      return;
    }

    const selectedIds = new Set(this.selectedPersons.map((person) => person.id));
    [...this.seatFieldDataSources.keys()]
      .filter((personId) => !selectedIds.has(personId))
      .forEach((personId) => this.seatFieldDataSources.delete(personId));

    for (const person of this.selectedPersons) {
      if (!this.seatFieldDataSources.has(person.id)) {
        this.seatFieldDataSources.set(person.id, this.buildSeatFieldDataSource(person.id));
        this.onSeatFieldChanged(person.id);
      }
    }
  }

  private buildSeatFieldDataSource(personId: string): AttributesEditorDataSource {
    const dataSource = new AttributesEditorDataSource();
    //configured order, not alphabetical
    dataSource.sortOnName = false;
    //the editor renders a read-only attribute as a label plus its value, and skips it entirely when
    //there's nothing to show — which is what we want for a position whose roster is locked
    const definitions = this.seatFieldsReadOnly
      ? this.position.seatFields.map((field) =>
          AttributeDefinition.parse({ ...field, isReadOnly: true })
        )
      : this.position.seatFields;

    dataSource.load(this.initialSeatFieldValues?.[personId] ?? [], definitions);
    return dataSource;
  }

  /**
   * Read-only accessor for the template. Building here as a fallback keeps change detection safe —
   * publishing values happens in syncSeatFieldDataSources, never during a render pass.
   */
  seatFieldDataSource(personId: string): AttributesEditorDataSource {
    let dataSource = this.seatFieldDataSources.get(personId);
    if (!dataSource) {
      dataSource = this.buildSeatFieldDataSource(personId);
      this.seatFieldDataSources.set(personId, dataSource);
    }
    return dataSource;
  }

  /**
   * Whether this person's editors will draw anything. The attributes editor skips a read-only field
   * with no value, so a locked position can produce an editor that renders nothing — and the wrapper
   * around it would still take up space. Mirrors the editor's own render condition.
   */
  seatFieldsVisible(personId: string): boolean {
    if (!this.hasSeatFields) {
      return false;
    }

    if (!this.seatFieldsReadOnly) {
      return true;
    }

    return (
      this.seatFieldDataSource(personId).editable?.some(
        (attribute) => attribute.isVisible && !attribute.isHidden && attribute.hasValue()
      ) ?? false
    );
  }

  /** ties a person's editors to an element id, so focus can find them once they render */
  seatFieldsId(personId: string): string {
    return `position-${this.position.id}-seat-fields-${personId}`;
  }

  /**
   * Sends focus to the first tracked field of a person who was just added. Their fields render above
   * the search box, so without this the applicant is left on the input with no sign that new — often
   * required — inputs just appeared further up the page.
   */
  private focusFirstSeatField(personId: string): void {
    if (!this.hasSeatFields || this.seatFieldsReadOnly) {
      return;
    }

    //the editors for this person are rendered by the change detection pass that follows, so the
    //field doesn't exist to focus until the next tick
    this.pendingSeatFieldFocus = setTimeout(() => {
      const editors = document.getElementById(this.seatFieldsId(personId));
      editors?.querySelector<HTMLElement>(SEAT_FIELD_CONTROL_SELECTOR)?.focus();
    });
  }

  onSeatFieldChanged(personId: string): void {
    const dataSource = this.seatFieldDataSources.get(personId);
    if (!dataSource || this.seatFieldsReadOnly) {
      return;
    }

    this.seatFieldValuesChange.emit({
      personId,
      attributes: dataSource.getWriteAttributes().attributes,
      answers: this.localPersistenceService.getAnswers(dataSource)
    });
  }

  /** Every rendered editor is valid — drives the submit guard alongside the min-seat check. */
  get seatFieldsValid(): boolean {
    if (!this.hasSeatFields || this.seatFieldsReadOnly) {
      return true;
    }

    return this.selectedPersons.every(
      (person) => this.seatFieldDataSources.get(person.id)?.isValid !== false
    );
  }

  private emitPersonIds(): void {
    this.personIdsChange.emit(this.selectedPersons.map((p) => p.id));
  }
}
